import { CheckCircle, Clock, Copy, Eye } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CadrageReadWithTeam } from '@/types';
import cadrageService from '@/services/cadrageService';

import notify from '@/lib/notify';
import { useTranslation } from 'react-i18next';

interface CadrageHistorySectionProps {
  cadrageId: string | undefined;
}

const CadrageHistorySection: React.FC<CadrageHistorySectionProps> = ({ cadrageId }) => {
  const { t, i18n } = useTranslation();
  const [history, setHistory] = useState<CadrageReadWithTeam[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadHistory = async () => {
      if (!cadrageId) return;

      setIsLoading(true);
      try {
        const versions = await cadrageService.getCadrageHistory(cadrageId);
        setHistory(versions);
      } catch (error) {
        console.error('Error loading cadrage history:', error);
        notify.error(t('cadrage.history.loadError', 'Erreur lors du chargement de l\'historique'));
      } finally {
        setIsLoading(false);
      }
    };

    loadHistory();
  }, [cadrageId]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCreateVersion = async () => {
    try {
      // Find the active version
      const activeVersion = history.find(v => v.is_active);
      if (!activeVersion) {
        notify.error(t('cadrage.history.noActiveVersion', 'Aucune version active trouvée'));
        return;
      }

      // Show loading notification
      notify.info(t('cadrage.history.creating', 'Création de la nouvelle version en cours...'));

      // Call API to create new version from active version
      const newVersion = await cadrageService.createNewVersion(activeVersion.id);

      // Show success notification
      notify.success(t('cadrage.history.created', 'Nouvelle version {{version}} créée avec succès', { version: newVersion.version }));

      // Navigate to the new version
      navigate(`/cadrages/${newVersion.id}`);
    } catch (error) {
      console.error('Error creating version:', error);
      const errorMessage = error instanceof Error ? error.message : t('cadrage.history.unknownError', 'Erreur inconnue');
      notify.error(t('cadrage.history.createError', 'Erreur lors de la création de la version: {{error}}', { error: errorMessage }));
    }
  };

  const getStatusBadgeColor = (status: string | null | undefined) => {
    const statusColors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      validated: 'bg-blue-100 text-blue-800',
      planned: 'bg-purple-100 text-purple-800',
      progress: 'bg-yellow-100 text-yellow-800',
      forge: 'bg-orange-100 text-orange-800',
      deployed: 'bg-green-100 text-green-800',
    };
    return statusColors[status || ''] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('cadrage.history.empty', 'Aucun historique disponible')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Clock className="h-6 w-6 text-primary" />
          <h2 className="text-xl font-semibold text-foreground">
            {t('cadrage.history.title', 'Historique des versions')}
          </h2>
        </div>

        {/* Bouton pour créer une nouvelle version depuis la version active */}
        <button
          onClick={handleCreateVersion}
          disabled={!history.find(v => v.is_active)}
          className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={t('cadrage.history.createButtonTitle', 'Créer une nouvelle version à partir de la version active')}
        >
          <Copy className="h-5 w-5" />
          <span>{t('cadrage.history.createButton', 'Créer une nouvelle version')}</span>
        </button>
      </div>

      <div className="space-y-3">
        {history.map((version) => (
          <div
            key={version.id}
            className={`border rounded-lg p-4 transition-all hover:shadow-md ${
              version.is_active
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="text-lg font-semibold text-foreground">
                    {t('cadrage.history.version', 'Version {{version}}', { version: version.version })}
                  </span>
                  {version.is_active && (
                    <span className="flex items-center space-x-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                      <CheckCircle className="h-4 w-4" />
                      <span>{t('cadrage.history.active', 'Active')}</span>
                    </span>
                  )}
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(version.status)}`}>
                    {version.status
                      ? t(`cadrage.general.statuses.${version.status}`, version.status)
                      : version.status}
                  </span>
                </div>

                <h3 className="text-md font-medium text-foreground mb-1">
                  {version.name}
                </h3>

                {version.description && (
                  <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                    {version.description}
                  </p>
                )}

                <div className="flex items-center space-x-4 text-xs text-muted-foreground mt-2">
                  {version.created_at && <span>{t('cadrage.history.createdOn', 'Créé le {{date}}', { date: formatDate(version.created_at) })}</span>}
                  {version.updated_at && version.created_at && version.updated_at !== version.created_at && (
                    <span>{t('cadrage.history.modifiedOn', 'Modifié le {{date}}', { date: formatDate(version.updated_at) })}</span>
                  )}
                  {version.members && version.members.length > 0 && (
                    <span>{t('cadrage.history.members', '{{count}} membre', { count: version.members.length })}</span>
                  )}
                </div>
              </div>

              <button
                onClick={() => navigate(`/cadrages/${version.id}`)}
                className="ml-4 flex items-center space-x-1 px-3 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
              >
                <Eye className="h-4 w-4" />
                <span>{t('global.view', 'Voir')}</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {history.length > 1 && (
        <div className="mt-4 pt-4 border-t border-border text-sm text-muted-foreground">
          <p>
            {t('cadrage.history.summary', 'Total : {{total}} version • Version active : {{active}}', {
              total: history.length,
              active: history.find(v => v.is_active)?.version || t('global.notAvailable', 'N/A')
            })}
          </p>
        </div>
      )}
    </div>
  );
};

export default CadrageHistorySection;
